import { hasValue } from "./data";
import { isBeforeToday, startOfDay, toDate } from "./status";

function getDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getEasterDate(year) {
  // Algoritmo de Gauss/Meeus (Computus). `posicaoCiclo = year % 19` (numero
  // aureo) — usar `year` direto aqui (e `yearInCentury` no monthFactor) jogava
  // a Pascoa e os feriados moveis (Carnaval, Sexta Santa, Corpus Christi) para
  // datas erradas.
  const posicaoCiclo = year % 19;
  const century = Math.floor(year / 100);
  const yearInCentury = year % 100;
  const leapCentury = Math.floor(century / 4);
  const remainderCentury = century % 4;
  const correction = Math.floor((century + 8) / 25);
  const adjustment = Math.floor((century - correction + 1) / 3);
  const goldenNumber = (19 * posicaoCiclo + century - leapCentury - adjustment + 15) % 30;
  const leapYearInCentury = Math.floor(yearInCentury / 4);
  const remainderYearInCentury = yearInCentury % 4;
  const weekdayOffset =
    (32 + 2 * remainderCentury + 2 * leapYearInCentury - goldenNumber - remainderYearInCentury) %
    7;
  const monthFactor =
    Math.floor((posicaoCiclo + 11 * goldenNumber + 22 * weekdayOffset) / 451);
  const month = Math.floor((goldenNumber + weekdayOffset - 7 * monthFactor + 114) / 31);
  const day = ((goldenNumber + weekdayOffset - 7 * monthFactor + 114) % 31) + 1;

  const easter = new Date(year, month - 1, day);
  easter.setHours(0, 0, 0, 0);
  return easter;
}

function shiftDays(date, days) {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  shifted.setHours(0, 0, 0, 0);
  return shifted;
}

const feriadosCache = new Map();
const feriadosNomeCache = new Map();

// Nomes na MESMA ordem das datas em `datasBase` abaixo.
const NOMES_FERIADOS_NACIONAIS = [
  "Confraternização Universal",
  "Carnaval (segunda)",
  "Carnaval",
  "Sexta-feira Santa",
  "Tiradentes",
  "Dia do Trabalho",
  "Corpus Christi",
  "Independência do Brasil",
  "Nossa Senhora Aparecida",
  "Finados",
  "Proclamação da República",
  "Natal",
];

// Feriado nacional pela Lei 14.759/2023, a partir de 2024. Fora da lista
// acima porque não vale para os anos anteriores (o histórico da planilha
// tem prazos de 2023). Mesmo nome do backend (`core/feriados.py`).
const CONSCIENCIA_NEGRA = "Dia Nacional de Zumbi e da Consciência Negra";
const ANO_INICIAL_CONSCIENCIA_NEGRA = 2024;

function getFeriadosNacionais(year) {
  if (feriadosCache.has(year)) {
    return feriadosCache.get(year);
  }

  const easter = getEasterDate(year);
  const datasBase = [
    new Date(year, 0, 1),
    shiftDays(easter, -48),
    shiftDays(easter, -47),
    shiftDays(easter, -2),
    new Date(year, 3, 21),
    new Date(year, 4, 1),
    shiftDays(easter, 60),
    new Date(year, 8, 7),
    new Date(year, 9, 12),
    new Date(year, 10, 2),
    new Date(year, 10, 15),
    new Date(year, 11, 25),
  ];

  const nomePorData = new Map();
  const chaves = datasBase.map((date, i) => {
    date.setHours(0, 0, 0, 0);
    const chave = getDateKey(date);
    nomePorData.set(chave, NOMES_FERIADOS_NACIONAIS[i]);
    return chave;
  });
  if (year >= ANO_INICIAL_CONSCIENCIA_NEGRA) {
    const chave = getDateKey(new Date(year, 10, 20));
    nomePorData.set(chave, CONSCIENCIA_NEGRA);
    chaves.push(chave);
  }

  const feriados = new Set(chaves);
  feriadosCache.set(year, feriados);
  feriadosNomeCache.set(year, nomePorData);
  return feriados;
}

export function parseNumero(value) {
  if (!hasValue(value)) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let normalized = String(value)
    .replace(/R\$/gi, "")
    .replace(/\s+/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!normalized || normalized === "-" || normalized === "--") {
    return null;
  }

  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");

  if (lastComma >= 0 && lastDot >= 0) {
    normalized =
      lastComma > lastDot
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.replace(/,/g, "");
  } else if (lastComma >= 0) {
    const decimalDigits = normalized.length - lastComma - 1;
    normalized =
      decimalDigits > 0 && decimalDigits <= 2
        ? normalized.replace(",", ".")
        : normalized.replace(/,/g, "");
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isFeriado(value, holidays = []) {
  const date = startOfDay(value);
  if (!date) {
    return false;
  }

  const dateKey = getDateKey(date);
  if (getFeriadosNacionais(date.getFullYear()).has(dateKey)) {
    return true;
  }

  return holidays.some((holiday) => {
    const holidayDate = startOfDay(holiday);
    return Boolean(holidayDate && getDateKey(holidayDate) === dateKey);
  });
}

export function getNomeFeriado(value, holidays = []) {
  const date = startOfDay(value);
  if (!date) {
    return null;
  }
  const dateKey = getDateKey(date);
  // garante que o cache de nomes do ano esteja populado
  getFeriadosNacionais(date.getFullYear());
  const nomePorData = feriadosNomeCache.get(date.getFullYear());
  if (nomePorData && nomePorData.has(dateKey)) {
    return nomePorData.get(dateKey);
  }
  // feriado custom (data sem nome conhecido) -> rotulo generico
  const ehCustom = holidays.some((holiday) => {
    const holidayDate = startOfDay(holiday);
    return Boolean(holidayDate && getDateKey(holidayDate) === dateKey);
  });
  return ehCustom ? "Feriado" : null;
}

export function isDiaUtil(value, holidays = []) {
  const date = startOfDay(value);
  if (!date) {
    return false;
  }

  const weekDay = date.getDay();
  return weekDay !== 0 && weekDay !== 6 && !isFeriado(date, holidays);
}

/**
 * Quantidade de DIAS ÚTEIS decorridos desde `dataEntrada` (exclusive) até hoje
 * (inclusive) — "dias em fila na etapa atual". Ignora fins de semana e feriados.
 * Retorna `null` se a data for inválida (o consumidor mostra empty-state),
 * e 0 se a entrada for hoje ou no futuro.
 */
export function diasUteisDecorridos(dataEntrada, holidays = []) {
  const inicio = startOfDay(dataEntrada);
  if (!inicio) {
    return null;
  }
  const hoje = startOfDay(new Date());
  if (!hoje || inicio >= hoje) {
    return 0;
  }
  let count = 0;
  const cursor = new Date(inicio);
  cursor.setDate(cursor.getDate() + 1);
  while (cursor <= hoje) {
    if (isDiaUtil(cursor, holidays)) {
      count += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/**
 * Texto da coluna "Dias em fila" a partir da data de entrada na etapa.
 * Retorna "—" (empty-state) quando nao ha data de entrada valida.
 */
export function fmtDiasEmFila(dataEntrada, holidays = []) {
  const dias = diasUteisDecorridos(dataEntrada, holidays);
  return dias == null ? "—" : String(dias);
}

export function diasUteis(dataInicial, dataFinal, holidays = []) {
  const inicio = startOfDay(dataInicial);
  const fim = startOfDay(dataFinal);
  if (!inicio || !fim) return null;

  let count = 0;
  const atual = new Date(inicio);

  while (atual <= fim) {
    if (isDiaUtil(atual, holidays)) count++;
    atual.setDate(atual.getDate() + 1);
  }

  return count;
}

export function addDiasUteis(data, dias, holidays = []) {
  const baseDate = startOfDay(data);
  if (!baseDate) return null;

  const totalDias = Number(dias) || 0;
  const resultado = new Date(baseDate);

  if (totalDias <= 0) {
    return resultado;
  }

  let adicionados = 0;

  while (adicionados < totalDias) {
    resultado.setDate(resultado.getDate() + 1);
    if (isDiaUtil(resultado, holidays)) adicionados++;
  }

  return resultado;
}

export function addDiasCorridos(data, dias) {
  const baseDate = startOfDay(data);
  if (!baseDate) {
    return null;
  }

  const resultado = new Date(baseDate);
  resultado.setDate(resultado.getDate() + (Number(dias) || 0));
  return resultado;
}

export function isData(valor) {
  return Boolean(toDate(valor)) && typeof valor !== "number";
}

export function formatarData(valor) {
  const date = toDate(valor);
  if (!date) return "—";
  return date.toLocaleDateString("pt-BR");
}

export function formatarMoeda(valor) {
  const numero = parseNumero(valor);
  if (numero === null) return "—";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numero);
}

export function isAtrasado(dataLimite) {
  return isBeforeToday(dataLimite);
}
